import React, { useMemo, useState } from 'react';

// A table in an answer, with a switch to see the same numbers as a chart.
//   Table    - the table as written (header row stays in view, see .chat-md-table in App.css)
//   Chart    - one bar per row for one number column (a picker when there are several number columns)
//   Heatmap  - for a grid of numbers (rows x several columns): every cell shaded by its size
// The numbers are read from the table itself (nothing is asked of the model), so any table the model writes can switch.
// A table with no number column simply shows no switch.

const textOf = (n) => (!n ? '' : n.type === 'text' ? n.value : (n.children || []).map(textOf).join(''));

const rowsOf = (node) => {
    const out = [];
    const walk = (n) => {
        if (!n) return;
        if (n.tagName === 'tr') {
            out.push((n.children || []).filter((c) => c.tagName === 'th' || c.tagName === 'td').map((c) => textOf(c).trim()));
            return;
        }
        (n.children || []).forEach(walk);
    };
    walk(node);
    return out;
};

// "24" -> 24, "1,200" -> 1200, "57%" -> 57, "24 (57%)" -> 24, "Python" -> null
const toNumber = (s) => {
    const m = String(s == null ? '' : s).replace(/,/g, '').match(/^[^\d\-.]?\s*(-?\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
};

const fmt = (v) => (Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100));

const BAR = 'var(--ndw-pri, #3b82f6)';
const LINE = '#e5e7eb';

function analyse(rows) {
    if (!rows || rows.length < 3) return null;                  // a header and at least two rows
    const head = rows[0];
    const body = rows.slice(1).filter((r) => r.length === head.length);
    if (body.length < 2) return null;
    const valueCols = [];
    const labelCols = [];
    head.forEach((h, i) => {
        const cells = body.map((r) => r[i]).filter((c) => c !== '' && c !== '-' && c !== '—');
        const nums = cells.map(toNumber).filter((v) => v !== null);
        const numeric = i > 0 && cells.length > 0 && nums.length / cells.length >= 0.6;
        if (numeric) {
            const pct = /%/.test(h) || cells.filter((c) => /%\s*\)?$/.test(c)).length / cells.length >= 0.6;
            valueCols.push({ index: i, title: h, pct });
        } else labelCols.push(i);
    });
    if (!valueCols.length || !labelCols.length) return null;
    const labelOf = (r) => labelCols.map((i) => r[i]).filter(Boolean).join(' · ');
    const items = body.map((r) => ({ label: labelOf(r), values: valueCols.map((c) => toNumber(r[c.index])) }));
    // The bars default to the first column that is a count, not a percentage.
    const firstCount = valueCols.findIndex((c) => !c.pct);
    return { valueCols, items, defaultCol: firstCount >= 0 ? firstCount : 0, labelTitle: labelCols.map((i) => head[i]).join(' · ') };
}

const segStyle = (on) => ({
    border: 'none', cursor: 'pointer', padding: '2px 10px', fontSize: 12, fontWeight: 600,
    background: on ? 'var(--ndw-pri-soft, #eaf1fb)' : 'transparent', color: on ? 'var(--ndw-pri, #194479)' : '#6b7280',
    borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0,
});

function Bars({ info, col }) {
    const c = info.valueCols[col];
    const max = Math.max(1, ...info.items.map((it) => it.values[col] || 0));
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '6px 2px' }}>
            {info.items.map((it, i) => {
                const v = it.values[col];
                return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <div title={it.label} style={{ flex: '0 0 38%', minWidth: 0, lineHeight: 1.25, overflow: 'hidden',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{it.label}</div>
                        <div style={{ flex: 1, minWidth: 0, background: '#f3f4f6', borderRadius: 4, height: 14 }}>
                            <div style={{ width: `${v == null ? 0 : Math.max(2, (v / max) * 100)}%`, height: '100%', background: BAR, borderRadius: 4 }} />
                        </div>
                        <div style={{ flex: '0 0 44px', textAlign: 'right', fontWeight: 600 }}>{v == null ? '-' : fmt(v)}{c.pct ? '%' : ''}</div>
                    </div>
                );
            })}
        </div>
    );
}

function Heat({ info }) {
    const max = Math.max(1, ...info.items.flatMap((it) => it.values.map((v) => v || 0)));
    const cell = { borderBottom: `1px solid ${LINE}`, borderRight: `1px solid ${LINE}`, padding: '4px 8px', textAlign: 'center', fontSize: 12.5 };
    return (
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
            <thead>
                <tr>
                    <th style={{ ...cell, textAlign: 'left', background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1, maxWidth: 120 }}>{info.labelTitle}</th>
                    {info.valueCols.map((c) => (
                        <th key={c.index} style={{ ...cell, background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1, maxWidth: 90, lineHeight: 1.25 }}>{c.title}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {info.items.map((it, i) => (
                    <tr key={i}>
                        <td style={{ ...cell, textAlign: 'left', maxWidth: 120, lineHeight: 1.25 }}>{it.label}</td>
                        {it.values.map((v, j) => (
                            <td key={j} style={{ ...cell, background: v ? `rgba(37, 99, 235, ${0.08 + 0.62 * (v / max)})` : 'transparent', color: v && v / max > 0.55 ? '#fff' : 'inherit' }}>
                                {v == null ? '' : fmt(v)}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const cellStyle = {
    borderBottom: `1px solid ${LINE}`, borderRight: `1px solid ${LINE}`, padding: '5px 8px', textAlign: 'left', whiteSpace: 'nowrap',
};

// A table sent by the backend as data ({ columns, rows }) instead of markdown: drawn the same way.
function DataTable({ rows }) {
    const [head, ...body] = rows;
    return (
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%', fontSize: 12.5 }}>
            <thead>
                <tr>{head.map((h, i) => <th key={i} style={{ ...cellStyle, background: '#f9fafb', fontWeight: 600, position: 'sticky', top: 0, zIndex: 1 }}>{h}</th>)}</tr>
            </thead>
            <tbody>
                {body.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} style={cellStyle}>{c}</td>)}</tr>)}
            </tbody>
        </table>
    );
}

// `data` (optional): a table the backend built from its own numbers - { view: 'table' | 'chart' | 'heatmap', title,
// columns, rows }. It opens on the view the backend suggested for its shape (or the one the person asked for); the
// switch still offers the others. Without `data` the table comes from the markdown (`node`) and opens as a table.
export default function ChatTable({ node, children, tableProps, data }) {
    const given = useMemo(() => (data && Array.isArray(data.columns) && Array.isArray(data.rows)
        ? [data.columns.map((c) => String(c ?? '')), ...data.rows.map((r) => (Array.isArray(r) ? r : []).map((c) => String(c ?? '')))]
        : null), [data]);
    const info = useMemo(() => analyse(given || rowsOf(node)), [given, node]);
    const heatOk = !!info && (info.valueCols.length >= 3 || (data?.view === 'heatmap' && info.valueCols.length >= 2));
    const start = data?.view === 'chart' && info ? 'chart' : data?.view === 'heatmap' && heatOk ? 'heat' : 'table';
    const [view, setView] = useState(start);
    const [col, setCol] = useState(null);
    const colIndex = col == null && info ? info.defaultCol : (col || 0);
    const views = info ? [['table', 'Table'], ['chart', 'Chart'], ...(heatOk ? [['heat', 'Heatmap']] : [])] : [];

    // The message bubble is only as wide as its text: a chart under a short sentence got a few pixels for its bars.
    // A chart / heatmap asks for a usable width (never more than the bubble can give).
    return (
        <div style={{ margin: '8px 0', ...(info ? { width: 360, maxWidth: '100%' } : {}) }}>
            {data?.title &&<div style={{ fontWeight: 600, fontSize: 13, margin: '4px 0' }}>{data.title}</div>}
            {info && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{ display: 'inline-flex', flexShrink: 0, gap: 2, border: `1px solid ${LINE}`, borderRadius: 8, padding: 2 }}>
                        {views.map(([k, label]) => (
                            <button key={k} type="button" onClick={() => setView(k)} style={segStyle(view === k)}>{label}</button>
                        ))}
                    </div>
                    {view === 'chart' && info.valueCols.length > 1 && (
                        <select value={colIndex} onChange={(e) => setCol(Number(e.target.value))}
                            style={{ fontSize: 12, border: `1px solid ${LINE}`, borderRadius: 6, padding: '2px 4px', maxWidth: '100%', minWidth: 0 }}>
                            {info.valueCols.map((c, i) => <option key={c.index} value={i}>{c.title}</option>)}
                        </select>
                    )}
                </div>
            )}
            <div className="chat-md-table" style={{ overflow: 'auto', maxWidth: '100%', maxHeight: 360 }}>
                {view === 'chart' && info ? <Bars info={info} col={colIndex} />
                    : view === 'heat' && info ? <Heat info={info} />
                    : given ? <DataTable rows={given} />
                    : <table {...tableProps} style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%', fontSize: 12.5 }}>{children}</table>}
            </div>
        </div>
    );
}

// The fenced block the backend adds under a reply (```idoks-view + JSON). While the reply is still being typed out the
// JSON is incomplete: nothing is drawn until it reads whole.
export function ViewBlock({ text }) {
    const data = useMemo(() => {
        try {
            const d = JSON.parse(text);
            return d && Array.isArray(d.columns) && Array.isArray(d.rows) ? d : null;
        } catch (e) {
            return null;
        }
    }, [text]);
    return data ? <ChatTable data={data} /> : null;
}

// The same block as plain text (for Copy): a markdown table.
export function viewBlocksAsText(text) {
    return String(text || '').replace(/```idoks-view\s*\n([\s\S]*?)\n```/g, (whole, json) => {
        try {
            const d = JSON.parse(json);
            const line = (r) => '| ' + r.map((c) => String(c ?? '').replace(/\|/g, '/')).join(' | ') + ' |';
            return [d.title || '', line(d.columns), line(d.columns.map(() => '---')), ...d.rows.map(line)].filter(Boolean).join('\n');
        } catch (e) {
            return '';
        }
    });
}
